import { useEffect, useMemo, useState } from 'react'
import { Modal } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import {
  createAccountingClient,
  linkAccountingClientUnit,
  listLinkableClientUnits,
  updateAccountingClient,
} from '@/modules/accounting/clients/accountingClientsService'
import type { AccountingClient, LinkableClientUnit } from '@/modules/accounting/clients/accountingClientTypes'

interface AccountingClientModalProps {
  officeUnitId: string
  client?: AccountingClient | null
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
}

type ClientMode = 'create' | 'link'

interface ClientForm {
  name: string
  legal_name: string
  trade_name: string
  cnpj: string
  tax_regime: string
  activity_kind: string
  whatsapp: string
  notes: string
  client_unit_id: string
}

const EMPTY_FORM: ClientForm = {
  name: '',
  legal_name: '',
  trade_name: '',
  cnpj: '',
  tax_regime: '',
  activity_kind: '',
  whatsapp: '',
  notes: '',
  client_unit_id: '',
}

const INPUT_CLASS = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-white'

export function AccountingClientModal({
  officeUnitId,
  client = null,
  isOpen,
  onClose,
  onSaved,
}: AccountingClientModalProps) {
  const { t } = useTranslation()
  const isEditing = Boolean(client)
  const [mode, setMode] = useState<ClientMode>('create')
  const [form, setForm] = useState<ClientForm>(EMPTY_FORM)
  const [linkableUnits, setLinkableUnits] = useState<LinkableClientUnit[]>([])
  const [loadingUnits, setLoadingUnits] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return

    setMode('create')
    setForm(client ? {
      name: client.name,
      legal_name: client.legal_name || '',
      trade_name: client.trade_name || '',
      cnpj: client.cnpj || '',
      tax_regime: client.tax_regime || '',
      activity_kind: client.activity_kind || '',
      whatsapp: client.whatsapp || '',
      notes: client.notes || '',
      client_unit_id: client.client_unit_id,
    } : EMPTY_FORM)
    setError(null)

    if (client) {
      setLinkableUnits([])
      setLoadingUnits(false)
      return
    }

    setLoadingUnits(true)

    listLinkableClientUnits(officeUnitId)
      .then(setLinkableUnits)
      .catch((loadError) => {
        console.error('[AccountingClientModal] Error loading units:', loadError)
        setLinkableUnits([])
      })
      .finally(() => setLoadingUnits(false))
  }, [client, isOpen, officeUnitId])

  const selectedUnit = useMemo(
    () => linkableUnits.find((unit) => unit.id === form.client_unit_id) || null,
    [form.client_unit_id, linkableUnits],
  )

  const updateField = (field: keyof ClientForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }))
    setError(null)
  }

  const handleUnitChange = (unitId: string) => {
    const unit = linkableUnits.find((candidate) => candidate.id === unitId)
    setForm((current) => ({
      ...current,
      client_unit_id: unitId,
      name: unit?.trade_name || unit?.legal_name || unit?.name || '',
    }))
    setError(null)
  }

  const handleSave = async () => {
    if (form.name.trim().length < 3) {
      setError(t('accounting.clients.form.errors.name'))
      return
    }

    if (mode === 'link' && !form.client_unit_id) {
      setError(t('accounting.clients.form.errors.unit'))
      return
    }

    setSaving(true)
    setError(null)

    try {
      if (client) {
        await updateAccountingClient(officeUnitId, client.party_id, form)
      } else if (mode === 'create') {
        await createAccountingClient(officeUnitId, form)
      } else {
        await linkAccountingClientUnit(officeUnitId, {
          client_unit_id: form.client_unit_id,
          name: form.name,
          whatsapp: form.whatsapp,
          notes: form.notes,
        })
      }

      onSaved()
      onClose()
    } catch (saveError: any) {
      console.error('[AccountingClientModal] Error saving client:', saveError)
      setError(saveError?.message || t('accounting.clients.form.errors.save'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t(isEditing ? 'accounting.clients.form.editTitle' : 'accounting.clients.form.title')}
      size="sm"
      onSubmit={handleSave}
      submitLabel={t('accounting.clients.form.save')}
      submitting={saving}
      closeLabel={t('actions.close')}
    >
      <div className="space-y-5">
        {!isEditing && <div className="grid grid-cols-2 rounded-lg bg-slate-100 p-1 dark:bg-slate-900">
          {(['create', 'link'] as ClientMode[]).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                setMode(option)
                setForm(EMPTY_FORM)
                setError(null)
              }}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                mode === option
                  ? 'bg-white text-emerald-700 shadow-sm dark:bg-slate-700 dark:text-emerald-300'
                  : 'text-slate-500 dark:text-slate-400'
              }`}
            >
              {t(`accounting.clients.form.mode.${option}`)}
            </button>
          ))}
        </div>}

        {mode === 'link' && (
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            {t('accounting.clients.form.existingUnit')}
            <select
              value={form.client_unit_id}
              onChange={(event) => handleUnitChange(event.target.value)}
              disabled={saving || loadingUnits}
              className={`${INPUT_CLASS} mt-1`}
            >
              <option value="">{t('accounting.clients.form.selectUnit')}</option>
              {linkableUnits.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.trade_name || unit.legal_name || unit.name}
                  {unit.cnpj ? ` — ${unit.cnpj}` : ''}
                </option>
              ))}
            </select>
            {!loadingUnits && linkableUnits.length === 0 && (
              <span className="mt-1 block text-xs text-slate-500">
                {t('accounting.clients.form.noUnits')}
              </span>
            )}
          </label>
        )}

        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
          {t('accounting.clients.form.name')} *
          <input
            value={form.name}
            onChange={(event) => updateField('name', event.target.value)}
            disabled={saving}
            className={`${INPUT_CLASS} mt-1`}
          />
        </label>

        {isEditing && (
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              {t('accounting.clients.form.legalName')}
              <input
                value={form.legal_name}
                onChange={(event) => updateField('legal_name', event.target.value)}
                disabled={saving}
                className={`${INPUT_CLASS} mt-1`}
              />
            </label>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              {t('accounting.clients.form.tradeName')}
              <input
                value={form.trade_name}
                onChange={(event) => updateField('trade_name', event.target.value)}
                disabled={saving}
                className={`${INPUT_CLASS} mt-1`}
              />
            </label>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 sm:col-span-2">
              {t('accounting.clients.form.cnpj')}
              <input
                value={form.cnpj}
                onChange={(event) => updateField('cnpj', event.target.value)}
                disabled={saving}
                className={`${INPUT_CLASS} mt-1`}
              />
            </label>
          </div>
        )}

        {isEditing && <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
          {t('accounting.clients.form.taxRegime')}
          <select
            value={form.tax_regime}
            onChange={(event) => updateField('tax_regime', event.target.value)}
            disabled={saving}
            className={`${INPUT_CLASS} mt-1`}
          >
            <option value="">{t('accounting.clients.form.taxRegimes.notInformed')}</option>
            {(['mei', 'simples_nacional', 'lucro_presumido', 'lucro_real', 'imune_isenta', 'other'] as const).map((taxRegime) => (
              <option key={taxRegime} value={taxRegime}>
                {t(`accounting.clients.form.taxRegimes.${taxRegime}`)}
              </option>
            ))}
          </select>
        </label>}

        {isEditing && <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
          {t('accounting.clients.form.activity')}
          <select
            value={form.activity_kind}
            onChange={(event) => updateField('activity_kind', event.target.value)}
            disabled={saving}
            className={`${INPUT_CLASS} mt-1`}
          >
            <option value="">{t('accounting.clients.form.activities.notInformed')}</option>
            {(['industrial', 'commerce', 'services', 'financial', 'real_estate', 'other'] as const).map((activity) => (
              <option key={activity} value={activity}>{t(`accounting.clients.form.activities.${activity}`)}</option>
            ))}
          </select>
          <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">{t('accounting.clients.form.activityHint')}</span>
        </label>}

        {isEditing && <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
          {t('accounting.clients.form.whatsapp')}
          <input
            type="tel"
            value={form.whatsapp}
            onChange={(event) => updateField('whatsapp', event.target.value)}
            disabled={saving}
            className={`${INPUT_CLASS} mt-1`}
          />
        </label>}

        {isEditing && <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
          {t('accounting.clients.form.notes')}
          <textarea
            rows={3}
            value={form.notes}
            onChange={(event) => updateField('notes', event.target.value)}
            disabled={saving}
            className={`${INPUT_CLASS} mt-1 resize-none`}
          />
        </label>}

        {mode === 'link' && selectedUnit && (
          <p className="rounded-lg bg-blue-50 p-3 text-xs text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
            {t('accounting.clients.form.linkHint')}
          </p>
        )}

        {error && (
          <p className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
            {error}
          </p>
        )}
      </div>
    </Modal>
  )
}
